import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Table, Tabs, Card, Typography, Tag, Button } from "antd";
import userService from "../../services/userService";
import masterService from "../../services/masterService";
import { useNotify } from "../../context/NotificationContext";
import { useCommon } from "../../context/CommonContext";
const { Text } = Typography;

const formatISODate = (value) => {
  if (!value) return "";
  try {
    return String(value).replace("T", "  ").replace(".000Z", "");
  } catch (e) {
    return String(value);
  }
};

const UserDetails = () => {
  const navigate = useNavigate();
  const { user_id } = useParams();
  const notify = useNotify();
  const { setPageTitle } = useCommon();
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState({});
  const [transactions, setTransactions] = useState([]);

  useEffect(() => {
    setPageTitle("User Details");
  }, []);

  const getUserDetails = useCallback(async (id) => {
    try {
      const resp = await userService.getUserDetails(id);
      const data = resp?.data;
      if (data && data.status) {
        setDetails(data.data || {});
      } else {
        notify.error({
          message: data?.message || "Failed to load user details",
        });
      }
    } catch (err) {
      console.error("Error fetching user details", err);
      notify.error({ message: "Error fetching user details" });
    } finally {
      setLoading(false);
    }
  }, []);

  const getUserTransactions = useCallback(async (id) => {
    setLoading(true);
    try {
      const resp = await userService.getUserTransactions({ user_id: id});
      const data = resp?.data;
      if (data && data.status) {
        console.log("getUserTransactions===>", data);
        const datatransactions = data?.data?.length > 0 ? data?.data : [];
        setTransactions(datatransactions || []);
      } else {
        notify.error({
          message: data?.message || "Failed to load transactions",
        });
      }
    } catch (err) {
      console.error("Error getting user Transactions", err);
      notify.error({ message: "Error getting Transactions" });
    } finally {
      setLoading(false);
    }
  }, []);

  const downloadInvoice = useCallback(
    async (paymentId) => {
      if (!paymentId) {
        notify.error({ message: "No payment found" });
        return;
      }
      // Open the tab synchronously within the click gesture; opening it after
      // the await gets blocked as a popup by the browser.
      const win = window.open("", "_blank");
      try {
        const resp = await masterService.downloadUserInvoice(
          user_id,
          paymentId
        );
        if (resp?.data?.status && resp?.data?.invoice_url) {
          if (win) {
            win.location.href = resp.data.invoice_url;
          } else {
            window.open(resp.data.invoice_url, "_blank");
          }
        } else {
          if (win) win.close();
          notify.info({
            message: resp?.data?.message || "Invoice not available",
          });
        }
      } catch (err) {
        if (win) win.close();
        console.error("Error in getting invoice", err);
        notify.error({ message: "Error in getting invoice" });
      }
    },
    [user_id]
  );

  useEffect(() => {
    if (!user_id) {
      navigate(-1);
      return;
    }
    setLoading(true);
    getUserDetails(user_id);
    getUserTransactions(user_id);
  }, [getUserDetails, getUserTransactions, navigate, user_id]);

  // Table column definitions
  const subscriptionColumns = [
    {
      title: "Plan Name",
      dataIndex: "plan_name",
      key: "plan_name",
      render: (text) => text || "-",
    },
    {
      title: "Plan Type",
      dataIndex: "plan_type_name",
      key: "plan_type_name",
      render: (text) => text || "-",
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      render: (price) => (price != null ? Number(price).toFixed(2) : "-"),
    },
    {
      title: "Currency",
      dataIndex: "currency_code",
      key: "currency_code",
      render: (text) => text || "-",
    },
    {
      title: "Payment ID",
      dataIndex: "fk_payment_id",
      key: "fk_payment_id",
    },
    {
      title: "Status",
      dataIndex: "subscription_status",
      key: "subscription_status",
      render: (status) => (
        <Tag
          color={status === "ACTIVE" || status === "TRIAL" ? "green" : "red"}
        >
          {status}
        </Tag>
      ),
    },
    {
      title: "Start Date",
      dataIndex: "subscription_start",
      key: "subscription_start",
      render: (text) => formatISODate(text),
    },
    {
      title: "End Date",
      dataIndex: "subscription_end",
      key: "subscription_end",
      render: (text) => formatISODate(text),
    },
    {
      title: "Autopay",
      dataIndex: "is_autopay",
      key: "is_autopay",
      render: (value) => (value ? "Yes" : "No"),
    },
    {
      title: "Admin Assigned",
      dataIndex: "admin_assigned",
      key: "admin_assigned",
      render: (value) => (value ? "Yes" : "No"),
    },
    {
      title: "Is Active",
      dataIndex: "is_active",
      key: "is_active",
      render: (value) => (
        <Tag color={value ? "green" : "red"}>{value ? "Active" : "Inactive"}</Tag>
      ),
    },
    {
      title: "Is Deleted",
      dataIndex: "is_deleted",
      key: "is_deleted",
      render: (value) => (
        <Tag color={value ? "red" : "green"}>{value ? "Yes" : "No"}</Tag>
      ),
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => formatISODate(text),
    },
  ];

  const favouritesColumns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Type",
      dataIndex: "show_type",
      key: "show_type",
      render: (type) => (
        <Tag color="blue">{type.charAt(0).toUpperCase() + type.slice(1)}</Tag>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
    {
      title: "Is Active",
      dataIndex: "favourite_is_active",
      key: "favourite_is_active",
      render: (status) => (
        <Tag color={status === "Y" ? "green" : "red"}>
          {status === "Y" ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Added At",
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => formatISODate(text),
    },
  ];

  const downloadsColumns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Type",
      dataIndex: "show_type",
      key: "show_type",
      render: (type) => (
        <Tag color="blue">{type.charAt(0).toUpperCase() + type.slice(1)}</Tag>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
    {
      title: "Downloaded At",
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => formatISODate(text),
    },
  ];

  const genresColumns = [
    {
      title: "Genre",
      dataIndex: "genre_name",
      key: "genre_name",
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "Description",
      dataIndex: "genre_description",
      key: "genre_description",
      ellipsis: true,
    },
    {
      title: "Preference Order",
      dataIndex: "preference_order_number",
      key: "preference_order_number",
    },
    {
      title: "Is Active",
      dataIndex: "is_active",
      key: "is_active",
      render: (value) => (
        <Tag color={value ? "green" : "red"}>{value ? "Active" : "Inactive"}</Tag>
      ),
    },
    {
      title: "Show in Preference",
      dataIndex: "show_in_preference",
      key: "show_in_preference",
      render: (status) => (
        <Tag color={status === "Y" ? "green" : "red"}>
          {status === "Y" ? "Yes" : "No"}
        </Tag>
      ),
    },
  ];

  const paymentSourceColumns = [
    {
      title: "Card",
      key: "card",
      render: (_, record) => {
        const label = record.brand || record.scheme || "Card";
        return record.last_four ? `${label} •••• ${record.last_four}` : label;
      },
    },
    {
      title: "Name on Card",
      dataIndex: "name_on_card",
      key: "name_on_card",
      render: (text) => text || "-",
    },
    {
      title: "Expiry",
      key: "expiry",
      render: (_, record) =>
        record.expiry_month && record.expiry_year
          ? `${record.expiry_month}/${record.expiry_year}`
          : "-",
    },
    {
      title: "Source",
      dataIndex: "payment_source",
      key: "payment_source",
      render: (text) => (text ? <Tag color="blue">{text}</Tag> : "-"),
    },
    {
      title: "Default",
      dataIndex: "is_default",
      key: "is_default",
      render: (value) => (
        <Tag color={value ? "green" : "default"}>{value ? "Yes" : "No"}</Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      render: (value) => (
        <Tag color={value ? "green" : "red"}>{value ? "Active" : "Inactive"}</Tag>
      ),
    },
    {
      title: "Deleted",
      dataIndex: "is_deleted",
      key: "is_deleted",
      render: (value) => (
        <Tag color={value ? "red" : "green"}>{value ? "Yes" : "No"}</Tag>
      ),
    },
    {
      title: "Added At",
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => text || "-",
    },
  ];

  const paymentColumns = [
    {
      title: "Payment ID",
      dataIndex: "payment_id",
      key: "payment_id",
    },
    {
      title: "Plan Name",
      dataIndex: "plan_name",
      key: "plan_name",
      render: (text) => text || "-",
    },
    {
      title: "Plan Type",
      dataIndex: "plan_type_name",
      key: "plan_type_name",
      render: (text) => text || "-",
    },
    {
      title: "Country",
      dataIndex: "country_name",
      key: "country_name",
      render: (text) => text || "-",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount) => (amount != null ? Number(amount).toFixed(2) : "-"),
    },
    {
      title: "Currency",
      dataIndex: "currency",
      key: "currency",
      render: (text) => text || "-",
    },
    {
      title: "Status",
      dataIndex: "payment_status",
      key: "payment_status",
      render: (status) => (
        <Tag color={status === "SUCCESS" ? "green" : status === "FAILED" ? "red" : "orange"}>
          {status || "-"}
        </Tag>
      ),
    },
    {
      title: "Source",
      dataIndex: "payment_source",
      key: "payment_source",
      render: (text) => text || "-",
    },
    {
      title: "Platform",
      dataIndex: "platform",
      key: "platform",
      render: (text) => text || "-",
    },
    {
      title: "Trigger",
      dataIndex: "payment_trigger",
      key: "payment_trigger",
      render: (text) => text || "-",
    },
    {
      title: "Invoice No.",
      dataIndex: "invoice_number",
      key: "invoice_number",
      render: (text) => text || "-",
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => text || "-",
    },
    {
      title: "Invoice",
      key: "invoice",
      render: (_, record) => (
        <Button
          size="small"
          disabled={!record.is_invoice_generated}
          onClick={() => downloadInvoice(record.payment_id)}
        >
          Invoice
        </Button>
      ),
    },
  ];

  const reservationColumns = [
    {
      title: "Reservation ID",
      dataIndex: "reservation_id",
      key: "reservation_id",
    },
    {
      title: "Plan Name",
      dataIndex: "plan_name",
      key: "plan_name",
      render: (text) => text || "-",
    },
    {
      title: "Plan Type",
      dataIndex: "plan_type_name",
      key: "plan_type_name",
      render: (text) => text || "-",
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      render: (price) => (price != null ? Number(price).toFixed(2) : "-"),
    },
    {
      title: "Currency",
      dataIndex: "currency",
      key: "currency",
      render: (text) => text || "-",
    },
    {
      title: "Action Type",
      dataIndex: "action_type",
      key: "action_type",
      render: (text) => (text ? <Tag color="blue">{text}</Tag> : "-"),
    },
    {
      title: "Duration (days)",
      dataIndex: "duration_days",
      key: "duration_days",
      render: (text) => text ?? "-",
    },
    {
      title: "Terms Accepted",
      dataIndex: "is_terms_accepted",
      key: "is_terms_accepted",
      render: (value) => (
        <Tag color={value ? "green" : "red"}>{value ? "Yes" : "No"}</Tag>
      ),
    },
    {
      title: "Promo Applied",
      dataIndex: "promo_applied",
      key: "promo_applied",
      render: (value) => (value ? "Yes" : "No"),
    },
    {
      title: "Active",
      dataIndex: "is_active",
      key: "is_active",
      render: (value) => (
        <Tag color={value ? "green" : "default"}>{value ? "Yes" : "No"}</Tag>
      ),
    },
    {
      title: "Expires At",
      dataIndex: "expires_at",
      key: "expires_at",
      render: (text) => text || "-",
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => text || "-",
    },
  ];

  const promoColumns = [
    {
      title: "Promo ID",
      dataIndex: "promo_id",
      key: "promo_id",
      render: (text) => text ?? "-",
    },
    {
      title: "Payment ID",
      dataIndex: "payment_id",
      key: "payment_id",
    },
    {
      title: "Promo Code",
      dataIndex: "promo_code",
      key: "promo_code",
      render: (text) => (text ? <Tag color="purple">{text}</Tag> : "-"),
    },
    {
      title: "Promo Name",
      dataIndex: "promo_name",
      key: "promo_name",
      render: (text) => text || "-",
    },
    {
      title: "Promo Type",
      key: "promo_type",
      render: (_, record) =>
        record.promo_discount_based ? (
          <Tag color="geekblue">Discount Based (%)</Tag>
        ) : record.promo_amount_based ? (
          <Tag color="gold">Amount Based (Fixed)</Tag>
        ) : (
          "-"
        ),
    },
    {
      title: "Campaign ID",
      dataIndex: "promo_campaign_id",
      key: "promo_campaign_id",
      render: (text) => text ?? "-",
    },
    {
      title: "Original Price",
      dataIndex: "original_plan_price",
      key: "original_plan_price",
      render: (v) => (v != null ? Number(v).toFixed(2) : "-"),
    },
    {
      title: "Discount",
      dataIndex: "discounted_amount",
      key: "discounted_amount",
      render: (v) => (v != null ? Number(v).toFixed(2) : "-"),
    },
    {
      title: "Final Price",
      dataIndex: "final_plan_price_after_discount",
      key: "final_plan_price_after_discount",
      render: (v) => (v != null ? Number(v).toFixed(2) : "-"),
    },
    {
      title: "Discount %",
      dataIndex: "promo_percent",
      key: "promo_percent",
      render: (v) => (v != null ? `${v}%` : "-"),
    },
    {
      title: "Fixed Amount",
      dataIndex: "promo_fixed_amount",
      key: "promo_fixed_amount",
      render: (v) => (v != null ? Number(v).toFixed(2) : "-"),
    },
    {
      title: "Max Discount",
      dataIndex: "promo_max_discount_amount",
      key: "promo_max_discount_amount",
      render: (v) => (v != null ? Number(v).toFixed(2) : "-"),
    },
    {
      title: "Min Txn Amount",
      dataIndex: "promo_min_txn_amount",
      key: "promo_min_txn_amount",
      render: (v) => (v != null ? Number(v).toFixed(2) : "-"),
    },
    {
      title: "Currency",
      dataIndex: "currency",
      key: "currency",
      render: (text) => text || "-",
    },
    {
      title: "Plan Name",
      dataIndex: "plan_name",
      key: "plan_name",
      render: (text) => text || "-",
    },
    {
      title: "Plan Type",
      dataIndex: "plan_type_name",
      key: "plan_type_name",
      render: (text) => text || "-",
    },
    {
      title: "Applied At",
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => text || "-",
    },
  ];

  // Tab items for Ant Design Tabs
  const tabItems = [
    {
      key: "subscriptions",
      label: "Subscriptions",
      children: (
        <Table
          columns={subscriptionColumns}
          dataSource={transactions}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 1200 }}
          locale={{ emptyText: "No subscriptions found" }}
        />
      ),
    },
    {
      key: "payments",
      label: "Payment Records",
      children: (
        <Table
          columns={paymentColumns}
          dataSource={details.payments || []}
          rowKey="payment_id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: "No payment records found" }}
          scroll={{ x: 1200 }}
        />
      ),
    },
    {
      key: "reservations",
      label: "Reservations",
      children: (
        <Table
          columns={reservationColumns}
          dataSource={details.reservations || []}
          rowKey="reservation_id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: "No reservations found" }}
          scroll={{ x: 1200 }}
        />
      ),
    },
    {
      key: "promos",
      label: "Promos",
      children: (
        <Table
          columns={promoColumns}
          dataSource={details.promos || []}
          rowKey="payment_id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: "No successful promo redemptions found" }}
          scroll={{ x: 2400 }}
        />
      ),
    },
    {
      key: "payment_sources",
      label: "Payment Source",
      children: (
        <Table
          columns={paymentSourceColumns}
          dataSource={details.payment_sources || []}
          rowKey="saved_payment_method_id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: "No saved payment methods found" }}
          scroll={{ x: 1100 }}
        />
      ),
    },
    {
      key: "favourites",
      label: "Favourite Movie/Series",
      children: (
        <Table
          columns={favouritesColumns}
          dataSource={details.favourites || []}
          rowKey={(record) => `${record.show_id}-${record.show_type}`}
          loading={loading}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: "No favourites found" }}
          scroll={{ x: 800 }}
        />
      ),
    },
    {
      key: "downloads",
      label: "Downloads",
      children: (
        <Table
          columns={downloadsColumns}
          dataSource={details.downloads || []}
          rowKey="download_id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: "No downloads found" }}
          scroll={{ x: 800 }}
        />
      ),
    },
    {
      key: "genres",
      label: "Genres",
      children: (
        <Table
          columns={genresColumns}
          dataSource={details.genres || []}
          rowKey="user_genre_id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: "No genre preferences found" }}
          scroll={{ x: 800 }}
        />
      ),
    },
  ];

  return (
    <div className="userdetails-page">
      {/* User Details Header */}
      <Card title="User Information" style={{ marginBottom: 24 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 16,
          }}
        >
          <div>
            <Text strong>User ID</Text>
            <div>{details.user_id || "-"}</div>
          </div>
          <div>
            <Text strong>First Name</Text>
            <div>{details.user_firstname || "-"}</div>
          </div>
          <div>
            <Text strong>Last Name</Text>
            <div>{details.user_lastname || "-"}</div>
          </div>
          <div>
            <Text strong>Email</Text>
            <div>{details.user_email || "-"}</div>
          </div>
          <div>
            <Text strong>Mobile</Text>
            <div>{details.user_mobile || "-"}</div>
          </div>
          <div>
            <Text strong>City</Text>
            <div>{details.user_city || "-"}</div>
          </div>
          <div>
            <Text strong>Country</Text>
            <div>{details.user_country || "-"}</div>
          </div>
          <div>
            <Text strong>Gender</Text>
            {/* Stored inconsistently across signup paths ("male", "Male", ...),
                so it is capitalised for display rather than shown raw. */}
            <div>
              {details.user_gender
                ? String(details.user_gender).charAt(0).toUpperCase() +
                  String(details.user_gender).slice(1).toLowerCase()
                : "-"}
            </div>
          </div>
          <div>
            <Text strong>Date of Birth</Text>
            {/* Arrives as a timestamp whose time half is always midnight, so the
                shared ISO formatter would append a meaningless 00:00:00. */}
            <div>
              {details.user_dob
                ? String(details.user_dob).split("T")[0].split(" ")[0]
                : "-"}
            </div>
          </div>
          <div>
            <Text strong>Status</Text>
            <div>
              <Tag color={details.user_is_active === "Y" ? "green" : "red"}>
                {details.user_is_active === "Y" ? "Active" : "Inactive"}
              </Tag>
            </div>
          </div>
          <div>
            <Text strong>Created At</Text>
            <div>{formatISODate(details.created_at)}</div>
          </div>
          <div>
            <Text strong>Updated At</Text>
            <div>{formatISODate(details.updated_at)}</div>
          </div>
        </div>
      </Card>

      {/* Tabs Section */}
      <Card>
        <Tabs defaultActiveKey="subscriptions" items={tabItems} />
      </Card>
    </div>
  );
};

export default UserDetails;
